// Shared builder for an event's PUBLIC website URL (the events-web site), reused by
// every Share / QR / marketing-link surface. The public site lives on a DIFFERENT
// origin than this management app (on.<brand>.com, not app.<brand>.com), so URLs must
// come from the brand's public-site origin, NOT window.location.origin (using the app
// origin pointed share links + QR codes back at the dashboard).
//
// A2e #101 (owner, 2026-10-05) — that origin is the WHITE LABEL's (`whiteLabelByDomain.siteUrl`: this domain's own
// site, else its brand's), handed in by BrandingContext — remembered per host, so it is known before the first render.
// It was the build's VITE_EVENTS_WEB_DOMAIN, which one build serving every brand could not get right.

let brandSiteUrl = '';

/** BrandingContext's seam: the public-site origin of the brand this dashboard is serving ('' = not known). */
export const setBrandSiteUrl = (url: string | null | undefined): void => {
  brandSiteUrl = String(url ?? '').trim();
};

/** The minimal event shape needed to build a public URL. */
export interface PublicUrlEvent {
  slug?: string | null;
  id?: string | null;
}

/**
 * The public site's origin as an absolute base (scheme included, no trailing slash),
 * e.g. `https://on.<brand>.com` — '' while the brand's is not known: NO link rather than
 * this app's own origin, which sent shared links and QR codes to the dashboard (review
 * 2026-10-05 — the very bug a separate public origin exists to prevent).
 */
export const eventsWebOrigin = (): string => {
  const raw = brandSiteUrl;

  if (!raw) {
    return '';
  }

  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;

  return withScheme.replace(/\/+$/, '');
};

/** The public slug/id segment for an event (slug preferred, id as fallback). */
export const eventPublicSlug = (event?: PublicUrlEvent | null, fallbackId?: string | null): string =>
  (event?.slug || event?.id || fallbackId || '').toString();

export interface BuildEventPublicUrlOptions {
  /** Used when the event has neither slug nor id loaded yet (e.g. the route param). */
  fallbackId?: string | null;
  /** Query params to append, e.g. a marketing-channel `{ ch: code }` marker. */
  query?: Record<string, string | number | null | undefined>;
}

/**
 * Build an event's public website URL, e.g. `https://on.eventsmagic.com/e/<slug>`.
 * Returns null when there is no slug/id to point at. Pass `query` to append params
 * (a marketing `ch` code, UTM tags, …).
 */
export const buildEventPublicUrl = (
  event?: PublicUrlEvent | null,
  options?: BuildEventPublicUrlOptions,
): string | null => {
  const slug = eventPublicSlug(event, options?.fallbackId);

  if (!slug) {
    return null;
  }

  const origin = eventsWebOrigin();

  if (!origin) {
    return null;
  }

  const base = `${origin}/e/${slug}`;
  const query = options?.query;

  if (!query) {
    return base;
  }

  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value !== null && value !== undefined && `${value}` !== '') {
      params.set(key, `${value}`);
    }
  });

  const qs = params.toString();

  return qs ? `${base}?${qs}` : base;
};

/**
 * Build an ORGANIZER page's public URL, e.g. `https://on.eventsmagic.com/o/<slug>`
 * (ROADMAP A2b #54). Returns null when there is no slug to point at.
 *
 * ⚠ SAME ORIGIN AS EVENTS, DELIBERATELY. The organizer page is served by the same
 * events-web app on the same host, so `em_visitor` — which is a HOST-ONLY cookie
 * (no `domain=`) — keeps identifying the same person as they move from an
 * organizer page to one of its events. A separate `organizers.` subdomain would
 * hand that visitor a second, unrelated identity.
 */
export const buildOrganizerPublicUrl = (slug?: string | null): string | null => {
  const value = (slug || '').toString().trim();

  if (!value) {
    return null;
  }

  const origin = eventsWebOrigin();

  if (!origin) {
    return null;
  }

  return `${origin}/o/${value}`;
};

/**
 * B24 item 5 — the organizer's "upcoming events" widget for another website (events-web `/o/<slug>/embed`): cards, or a
 * compact list for a sidebar.
 */
export const organizerEmbedUrl = (publicUrl: string, layout: 'cards' | 'list'): string =>
  `${publicUrl}/embed${layout === 'list' ? '?layout=list' : ''}`;

/**
 * The `<iframe>` a host pastes into their own website. ⚠ Every attribute value is HTML-escaped: the organizer's NAME
 * lands in `title`, and a name with a quote in it would otherwise end the attribute and write markup into their page.
 */
export const organizerEmbedSnippet = (publicUrl: string, organizerName: string, layout: 'cards' | 'list'): string => {
  const attr = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const height = layout === 'list' ? 480 : 720;

  return `<iframe src="${attr(organizerEmbedUrl(publicUrl, layout))}" title="${attr(`Upcoming events — ${organizerName}`)}" width="100%" height="${height}" style="border:0" loading="lazy"></iframe>`;
};
